import React, { memo } from "react";
import { Skeleton } from "@progress/kendo-react-indicators";

import CommonCard from "src/components/Card/Card";
import { ChartTabStrip } from "../ChartTabStrip";

import { TOGGLE_THEME } from "./chartConstants";
import "./ChartCard.scss";
import { DropDownList } from "@progress/kendo-react-dropdowns";

/*
 * Shared shell for the three left-column dashboard charts: title, optional
 * subtitle, the pill toggles on the right, and the loading / empty / content
 * ladder. Following src/pages/eventDashboard/DashboardChart.jsx, loading is a
 * Skeleton rather than the legacy k-loading-mask overlay.
 */

const ChartCardLoader = () => (
  <div className="chart-card__loader">
    <Skeleton shape="text" style={{ width: "45%", marginBottom: 8 }} />
    <Skeleton shape="text" style={{ width: "30%", marginBottom: 18 }} />
    <Skeleton shape="rectangle" style={{ width: "100%", height: "210px" }} />
  </div>
);

/**
 * One pill toggle. `options` are `{ label, value }`; selection is by value so
 * callers never deal in indices.
 */
export const ChartToggle = memo(({ options, value, onChange, ariaLabel }) => {
  const selectedIndex = Math.max(
    0,
    options.findIndex((option) => option.value === value),
  );

  return (
    <div className="chart-card__toggle" style={{ width: options.length * 100 }}>
      <ChartTabStrip
        labels={options.map((option) => option.label)}
        accessibilityLabels={options.map((option) => `${ariaLabel}: ${option.label}`)}
        selectedIndex={selectedIndex}
        onSelect={(index) => onChange(options[index].value)}
        {...TOGGLE_THEME}
      />
    </div>
  );
});

ChartToggle.displayName = "ChartToggle";

const ChartCard = ({
  title,
  subtitle,
  toggles,
  yearOptions = [],
  selectedYear,
  onYearChange,
  periodTypeLabel = "Year",
  positionOptions = [],
  selectedPosition,
  onPositionChange,
  positionLabel = "Position",
  isLoading = false,
  isEmpty = false,
  emptyMessage = "No Data Found",
  className,
  children,
}) => {
  return <CommonCard className={`chart-card ${className || ''}`}>
    <div className="chart-card__head">
      <div className="chart-card__heading">
        <span className="chart-card__title">{title}</span>
        {subtitle ? <span className="chart-card__subtitle">{subtitle}</span> : null}
      </div>
      <div className="chart-card__controls">
        {positionOptions.length > 0 && (
          <div className="chart-card__dropdown-wrapper">
            <label className="chart-card__dropdown-label">{positionLabel}</label>
            <DropDownList
              data={positionOptions}
              dataItemKey="id"
              textField="text"
              value={selectedPosition}
              onChange={(e) => onPositionChange(e.value)}
              className="chart-card__position-dropdown"
            />
          </div>
        )}
        {yearOptions.length > 0 && (
          <div className="chart-card__dropdown-wrapper">
            <label className="chart-card__dropdown-label">{periodTypeLabel}</label>
            <DropDownList
              data={yearOptions}
              dataItemKey="value"
              textField="text"
              value={selectedYear}
              onChange={(e) => onYearChange(e.value)}
              className="chart-card__year-dropdown"
            />
          </div>
        )}
        {toggles ? <div className="chart-card__toggles">{toggles}</div> : null}
      </div>
    </div>

    {isLoading ? (
      <ChartCardLoader />
    ) : isEmpty ? (
      <div className="chart-card__nodata">{emptyMessage}</div>
    ) : (
      children
    )}
  </CommonCard>
};

export default ChartCard;
